import tenantTheme from '@theme';
import styled, { css } from 'styled-components';

export const PoolCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${tenantTheme['border-color-light']};
  border-radius: ${tenantTheme['card-radius']};
  .pool-big {
    font-size: 22px;
  }
  .pool-sub {
    font-size: 12px;
    color: ${tenantTheme['text-color-secondary']};
  }
  .pool-cells {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
    border-top: 1px solid ${tenantTheme['border-color-light']};
    /* tinted icon badge + number on one row, the name below */
    > div {
      display: grid;
      grid-template-columns: 22px minmax(0, 1fr);
      align-items: center;
      gap: 6px 8px;
      padding: 14px 6px;
      padding-inline-start: 12px;
      border-inline-start: 1px solid ${tenantTheme['border-color-light']};
    }
    > div:first-child {
      border-inline-start: 0;
      padding-inline-start: 0;
    }
    .pool-ic {
      display: inline-grid;
      place-items: center;
      width: 22px;
      height: 22px;
      border-radius: ${tenantTheme['border-radius-base']};
      background: color-mix(in srgb, var(--ic-tint) 14%, ${tenantTheme['white-color']});
    }
    b {
      font-size: 15px;
      line-height: 18px;
    }
    .pool-label {
      grid-column: 1 / -1;
      font-size: 12px;
      line-height: 15px;
      color: ${tenantTheme['text-color-secondary']};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  /* phones: a two-column list (icon, name, count), no dividers */
  .pool-cells.is-mobile {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 16px;
    padding-top: 12px;
    > div,
    > div:first-child {
      grid-template-columns: 22px minmax(0, 1fr) auto;
      gap: 8px;
      padding: 0;
      border: 0;
    }
    b {
      order: 3;
      font-size: 14px;
    }
    .pool-label {
      grid-column: auto;
      order: 2;
      font-size: 12px;
      color: ${tenantTheme['text-color']};
    }
  }
`;

export const AgentCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${tenantTheme['border-color-light']};
  border-radius: ${tenantTheme['card-radius']};
  background: ${tenantTheme['white-color']};
  text-align: start;
  font: inherit;
  color: inherit;
  &.is-click {
    cursor: pointer;
    transition:
      border-color 0.15s,
      background-color 0.15s;
  }
  &.is-click:hover {
    border-color: ${tenantTheme['primary-color']};
    background: ${tenantTheme['primary-light-4']};
  }
  /* phones: a list with dividers instead of boxed cards */
  &.is-row {
    align-items: flex-start;
    border: 0;
    border-bottom: 1px solid ${tenantTheme['border-color-light']};
    border-radius: 0;
    padding: 12px 0;
  }
  &.is-row:hover {
    background: transparent;
  }
  /* phones: the agent's products in two even columns so the icons line up; long names are cut with "…" */
  &.is-row .agent-chips {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  &.is-row .agent-chips > span {
    min-width: 0;
  }
  &.is-row .chip-name {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .avatar {
    flex: none;
    width: 36px;
    height: 36px;
    line-height: 34px;
    background: ${tenantTheme['primary-light-4']};
    color: ${tenantTheme['primary-color']};
    font-size: 12px;
    font-weight: 700;
  }
  .agent-body {
    flex: 1;
    min-width: 0;
    b {
      display: block;
    }
    small {
      font-size: 12px;
      color: ${tenantTheme['text-color-secondary']};
    }
  }
`;

// The columns live on the table and every row takes them through subgrid, so the header (Has, Change, After) sits
// over its column; with a grid per row the auto Change column was as wide as its text in the header only.
export const AssignTable = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 48px auto 96px;
  column-gap: 12px;
  /* phones: one line per product (name left, − value + and Max right) */
  ${({ $mobile }) =>
    $mobile &&
    css`
      grid-template-columns: minmax(0, 1fr) auto;
      column-gap: 10px;
    `}
  .assign-row {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    align-items: center;
    padding: 10px 0;
    border-top: 1px solid ${tenantTheme['border-color-light']};
  }
  .assign-pill {
    border: 1px solid ${tenantTheme['border-color-light']};
    border-radius: 99px;
    padding: 0 2px;
  }
  .assign-head {
    border-top: 0;
    padding-top: 0;
    font-size: 12px;
    color: ${tenantTheme['text-color-secondary']};
  }
  .assign-section {
    grid-column: 1 / -1;
    padding: 14px 0 6px;
    font-size: 12px;
    font-weight: 700;
    color: ${tenantTheme['text-color-secondary']};
  }
  .assign-name {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    b {
      display: block;
    }
    small {
      font-size: 12px;
      color: ${tenantTheme['text-color-secondary']};
    }
  }
  .assign-after {
    text-align: end;
    line-height: 1.2;
    b {
      display: block;
    }
    small {
      font-size: 11px;
      color: ${tenantTheme['text-color-secondary']};
    }
  }
  /* antd's RTL input sets direction: rtl itself, which drew "-3" as "3-" */
  .ant-input-number-input {
    text-align: center;
    direction: ltr;
  }
`;
